/**
 * 1.8.0 (D-1.8.0-2, D-1.8.0-3, D-1.8.0-4): what an answer shows, by kind.
 *   clarify — the short question and its chips (and "Something else", which hands the box back for words);
 *   steps   — the role line first, the sentence, then numbered steps, each with its screen and a Show me;
 *   here    — "What can I do here?": five lines at most, each a thing to press, with Show me;
 *   none    — "The app doesn't do that today", and the nearest thing as a chip.
 * Every answer ends with "Based on version 1.8.0's guide" and the thumbs. Chips are buttons; the conversation is a log.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AssistantAnswer, AssistantShowMe, AssistantThread } from '@shared/assistant';
import { sendAssistantFeedback } from '@/lib/assistant';
import { runShowMe } from '@/lib/showme';
import { Icon } from './Icons';
import { Button } from './ui/button';
import { useAnnounce } from './Announcer';

/** What the next press sends: a chip of a question, or a "not today" nearest path. */
export type Press = { kind: 'pick'; id: string; label: string; thread: AssistantThread } | { kind: 'path'; label: string; thread: AssistantThread } | { kind: 'else'; thread: AssistantThread };

function ShowMeButton({ target, onNavigate, testId }: { target: AssistantShowMe; onNavigate?: () => void; testId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const announce = useAnnounce();
  return (
    <Button
      size="sm"
      variant="secondary"
      aria-label={t('assistant.showMeOn', { screen: target.label })}
      data-testid={testId}
      onClick={() => {
        onNavigate?.();
        runShowMe(target, navigate, t('assistant.here'), (found) => announce(found ? t('assistant.shown', { screen: target.label }) : t('assistant.shownPage', { screen: target.label })));
      }}
    >
      <Icon name="arrow" />
      {t('assistant.showMe')}
    </Button>
  );
}

/** "Based on version 1.8.0's guide" and the thumbs (ASSISTANT_RATED, the question with the person taken out). */
export function VersionAndThumbs({ answer, question }: { answer: AssistantAnswer; question: string }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const [voted, setVoted] = useState<boolean | null>(null);
  const vote = (helpful: boolean) => {
    setVoted(helpful);
    announce(t('assistant180.thanks'));
    void sendAssistantFeedback(helpful, { question, kind: answer.kind ?? 'answer', path: typeof window === 'undefined' ? undefined : window.location.pathname }).catch(() => undefined);
  };
  return (
    <>
      {answer.version ? (
        <p className="help ask-version" data-testid="assistant-version">
          {t('assistant180.basedOn', { version: answer.version })}
        </p>
      ) : null}
      <div className="row assistant-vote" role="group" aria-label={t('assistant.helpful')}>
        <span aria-hidden="true">{t('assistant.helpful')}</span>
        {voted === null ? (
          <div className="chips">
            <Button size="sm" variant="ghost" onClick={() => vote(true)} aria-label={t('assistant180.thumbUp')} data-testid="assistant-yes">
              <span aria-hidden="true">👍</span>
              {t('assistant.yes')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => vote(false)} aria-label={t('assistant180.thumbDown')} data-testid="assistant-no">
              <span aria-hidden="true">👎</span>
              {t('assistant.no')}
            </Button>
          </div>
        ) : (
          <span className="help">{t('assistant180.thanks')}</span>
        )}
      </div>
    </>
  );
}

/** A short question and its chips (and "Something else", which hands the box back for words). */
export function ChipsView({ clarify: c, onPress, live }: { clarify: NonNullable<AssistantAnswer['clarify']>; onPress: (p: Press) => void; live: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="ask-clarify" data-testid="assistant-clarify">
      <p className="ask-q">{c.question}</p>
      <div className="chips" role="group" aria-label={c.question}>
        {c.chips.map((chip) => (
          <Button key={chip.id} size="sm" variant="secondary" disabled={!live} onClick={() => onPress({ kind: 'pick', id: chip.id, label: chip.label, thread: c.thread })} data-testid={`assistant-chip-${chip.id}`}>
            {chip.label}
          </Button>
        ))}
        <Button size="sm" variant="ghost" disabled={!live} onClick={() => onPress({ kind: 'else', thread: c.thread })} data-testid="assistant-chip-else">
          {t('assistant180.somethingElse')}
        </Button>
      </div>
    </div>
  );
}

/** 2 Nov 2026 (D-1103-ASK-5): a data answer — the sentence, then the list, each line with one button that opens its screen. */
export function DataView({ answer, onNavigate }: { answer: AssistantAnswer; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const d = answer.data;
  if (!d) return null;
  return (
    <div className="ask-data" data-testid="assistant-data">
      <p data-testid="assistant-data-sentence">{d.sentence}</p>
      {d.items.length ? (
        <ul className="ask-step-list" aria-label={t('askdata1103.list')} data-testid="assistant-data-list">
          {d.items.map((i, n) => (
            <li key={`${n}-${i.route}`} data-testid="assistant-data-line">
              <span className="ask-step-text">{i.label}</span>
              <Button
                size="sm"
                variant="secondary"
                aria-label={`${i.button}: ${i.label}`}
                data-testid="assistant-data-open"
                onClick={() => {
                  onNavigate?.();
                  navigate(i.route);
                }}
              >
                <Icon name="arrow" />
                {i.button}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {d.more > 0 ? <p className="help">{t('askdata1103.more', { count: d.more })}</p> : null}
      <p className="help">{t('askdata1103.readOnly')}</p>
    </div>
  );
}

/** The 1.8.0 kinds; null for an ordinary answer (AnswerView shows those). */
export function KindView({ answer, onPress, onNavigate, live }: { answer: AssistantAnswer; onPress: (p: Press) => void; onNavigate?: () => void; live: boolean }) {
  const { t } = useTranslation();
  if (answer.kind === 'data' && answer.data) return <DataView answer={answer} onNavigate={onNavigate} />;
  if (answer.kind === 'clarify' && answer.clarify) return <ChipsView clarify={answer.clarify} onPress={onPress} live={live} />;
  if (answer.kind === 'steps' && answer.steps) {
    return (
      <div className="ask-steps" data-testid="assistant-steps">
        {answer.roleLine ? (
          <p className={answer.allowed === false ? 'warn take ask-role' : 'help ask-role'} data-testid="assistant-role-line">
            <Icon name={answer.allowed === false ? 'lock' : 'check'} />
            <span>{answer.roleLine}</span>
          </p>
        ) : null}
        <p>{answer.answer.split('\n')[0]}</p>
        <ol className="ask-step-list">
          {answer.steps.map((s) => (
            <li key={s.n} data-testid="assistant-step">
              <span className="eyebrow">{t('assistant180.onScreen', { screen: s.screen })}</span>
              <span className="ask-step-text">{s.text}</span>
              {s.see ? <span className="help">{t('assistant180.youWillSee', { see: s.see })}</span> : null}
              {s.showMe ? <ShowMeButton target={s.showMe} onNavigate={onNavigate} testId={`assistant-step-show-${s.n}`} /> : null}
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (answer.kind === 'here' && answer.here) {
    return (
      <div className="ask-here" data-testid="assistant-here">
        <p>{answer.answer.split('\n')[0]}</p>
        <ul className="ask-step-list">
          {answer.here.map((h, i) => (
            <li key={h.control} data-testid="assistant-here-line">
              <span className="ask-step-text">
                <strong>“{h.control}”</strong> — {h.does}
              </span>
              {h.showMe ? <ShowMeButton target={h.showMe} onNavigate={onNavigate} testId={`assistant-here-show-${i + 1}`} /> : null}
            </li>
          ))}
        </ul>
        {!answer.here.length ? <p className="help">{t('assistant180.hereNothing')}</p> : null}
      </div>
    );
  }
  if (answer.kind === 'none') {
    return (
      <div className="ask-none" data-testid="assistant-none">
        <p>{answer.answer}</p>
        {answer.nearest ? (
          <div className="chips">
            <Button size="sm" variant="secondary" disabled={!live} onClick={() => onPress({ kind: 'path', label: answer.nearest!.label, thread: answer.nearest!.thread })} data-testid="assistant-nearest">
              <Icon name="arrow" />
              {t('assistant180.nearest', { label: answer.nearest.label })}
            </Button>
          </div>
        ) : null}
      </div>
    );
  }
  return null;
}
